import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, ArrowLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
	Navigation,
	Pagination,
	Autoplay,
	Zoom,
	Keyboard,
} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import './styles.scss';

const EventDetail = () => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);

	const event = {
		id: 1,
		date: t('events.date'),
		title: t('events.detailTitle'),
		location: t('events.location'),
		image: '/assets/ik/ik1.jpg',
		fullDescription: t('events.detailDesc'),
		gallery: [
			'/assets/ik/ik7.png',
			'/assets/ik/ik1.jpg',
			'/assets/ik/ik9.png',
			'/assets/ik/ik3.jpg',
			'/assets/ik/ik4.jpg',
			'/assets/ik/ik6.png',
			'/assets/ik/ik8.png',
			'/assets/ik/ik10.png',
		],
	};

	const eventSliderGallery = [
		'/assets/ik/ik2.jpg',
		'/assets/ik/ik3.jpg',
		'/assets/ik/ik6.png',
		'/assets/ik/ik7.png',
		'/assets/ik/ik8.png',
		'/assets/ik/ik9.png',
	]

	const handleBack = () => {
		navigate(-1);
	};

	const openModal = (index) => {
		setCurrentImageIndex(index);
		setIsModalOpen(true);
		document.body.style.overflow = 'hidden';
	};

	const closeModal = () => {
		setIsModalOpen(false);
		document.body.style.overflow = 'auto';
	};

	// Обработка клавиши Escape для закрытия модалки
	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === 'Escape' && isModalOpen) {
				closeModal();
			}
		};

		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [isModalOpen]);

	// Обработка клика по оверлею с проверкой на мобильных устройствах
	const handleModalClick = (e) => {
		// Проверяем, что клик был именно по оверлею, а не по контенту
		if (e.target === e.currentTarget) {
			closeModal();
		}
	};

	// Обработка touch событий для мобильных устройств
	const handleTouchStart = (e) => {
		if (e.target === e.currentTarget) {
			closeModal();
		}
	};

	return (
		<section className='event-detail'>
			<div className='container'>
				<motion.div
					className='event-detail__header'
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
				>
					<button onClick={handleBack} className='event-detail__back-btn'>
						<ArrowLeft size={20} />
					</button>
				</motion.div>
				<motion.div
					className='event-detail__content'
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.2 }}
				>
					<div className='event-detail__image-wrapper'>
						<Swiper
							tabIndex={0}
							modules={[Navigation, Pagination, Autoplay, Keyboard]}
							navigation={true}
							slidesPerView={1}
							spaceBetween={10}
							breakpoints={{
								640: {
									slidesPerView: 2,
									spaceBetween: 20,
								},
								768: {
									slidesPerView: 2,
									spaceBetween: 10,
								},
								1024: {
									slidesPerView: 2.5,
									spaceBetween: 20,
								},
							}}
							keyboard={{
								enabled: true,
								onlyInViewport: true,
								pageUpDown: true,
							}}
							pagination={{
								clickable: true,
							}}
							autoplay={{
								delay: 4000,
								disableOnInteraction: false,
							}}
							loop={true}
							className='event-detail__swiper'
						>
							{eventSliderGallery.map((image, index) => (
								<SwiperSlide key={index}>
									<img
										src={image}
										alt={`${event.title} ${index + 1}`}
										className='event-detail__image'
									/>
								</SwiperSlide>
							))}
						</Swiper>
					</div>

					<div className='event-detail__info'>
						<div className='event-detail__main-info'>
							<h1 className='event-detail__title'>{event.title}</h1>
							<div className='event-detail__meta'>
								<div className='event-detail__meta-item'>
									<CalendarDays size={18} />
									<span>{event.date}</span>
								</div>
								<div className='event-detail__meta-item'>
									<MapPin size={18} />
									<span>{event.location}</span>
								</div>
							</div>
						</div>
					</div>

					<div className='event-detail__description'>
						<h2>{t('events.aboutEvent')}</h2>
						<p className='event-detail__description-content'>
							{event.fullDescription}
						</p>
					</div>

					{event.gallery && event.gallery.length > 1 && (
						<div className='event-detail__gallery'>
							<h2>{t('events.gallery')}</h2>
							<div className='event-detail__gallery-grid'>
								{event.gallery.map((image, index) => (
									<motion.div
										key={index}
										className='event-detail__gallery-item'
										whileHover={{ scale: 1.05 }}
										onClick={() => openModal(index)}
									>
										<img
											src={image}
											alt={`${t('events.gallery')} ${index + 1}`}
										/>
									</motion.div>
								))}
							</div>
						</div>
					)}
				</motion.div>
			</div>

			{isModalOpen && (
				<div
					className='event-detail__modal'
					onClick={handleModalClick}
					onTouchStart={handleTouchStart}
				>
					<div
						className='event-detail__modal-content'
						onClick={(e) => e.stopPropagation()}
					>
						<button className='event-detail__modal-close' onClick={closeModal}>
							<X size={24} />
						</button>
						<Swiper
							modules={[Navigation, Pagination, Zoom, Keyboard]}
							spaceBetween={0}
							keyboard={{
								enabled: true,
								onlyInViewport: true,
								pageUpDown: true,
							}}
							slidesPerView={1}
							navigation={true}
							pagination={{
								clickable: true,
							}}
							zoom={{
								maxRatio: 3,
								minRatio: 1,
							}}
							initialSlide={currentImageIndex}
							className='event-detail__modal-swiper'
						>
							{event.gallery.map((image, index) => (
								<SwiperSlide key={index}>
									<div className='swiper-zoom-container'>
										<img
											src={image}
											alt={`${t('events.gallery')} ${index + 1}`}
											className='event-detail__modal-image'
										/>
									</div>
								</SwiperSlide>
							))}
						</Swiper>
					</div>
				</div>
			)}
		</section>
	);
};

export default EventDetail;
